import { UnderlineStyleType } from '../administration/template-builder/template-editor-page/models/template-builder-text-theme.model';
import { LookupKeys } from 'ag-common-lib/lib/models/crm/lookup.model';

export const LINK_STYLE_FROM_GLOBAL = '__from_global__';

export const HTML_EDITOR_LINK_STYLE_CSS_VARS = {
  linkColor: '--link-color',
  linkTextDecoration: '--link-text-decoration',
  linkTextDecorationStyle: '--link-text-decoration-style',
  activeLinkColor: '--active-link-color',
  activeLinkTextDecoration: '--active-link-text-decoration',
  activeLinkTextDecorationStyle: '--active-link-text-decoration-style',
} as const;

const LINK_STYLE_CSS_VAR_VALUES = new Set<string>(Object.values(HTML_EDITOR_LINK_STYLE_CSS_VARS));

// solid, wavy, dotted, dashed, double
export const LINK_UNDERLINE_SELECT_OPTIONS = [
  { [LookupKeys.value]: LINK_STYLE_FROM_GLOBAL, [LookupKeys.description]: 'From Global styles' },
  { [LookupKeys.value]: UnderlineStyleType.solid, [LookupKeys.description]: 'Solid' },
  { [LookupKeys.value]: UnderlineStyleType.dotted, [LookupKeys.description]: 'Dotted' },
  { [LookupKeys.value]: UnderlineStyleType.wavy, [LookupKeys.description]: 'Wavy' },
  { [LookupKeys.value]: UnderlineStyleType.none, [LookupKeys.description]: 'None' },
];

export type LinkColorValue = string | typeof LINK_STYLE_FROM_GLOBAL;
export type LinkUnderlineValue = UnderlineStyleType | typeof LINK_STYLE_FROM_GLOBAL;

export interface LinkStylingFormValues {
  linkColor?: LinkColorValue | null;
  linkUnderline?: LinkUnderlineValue | null;
  mouseoverColor?: LinkColorValue | null;
  mouseoverUnderline?: LinkUnderlineValue | null;
}

export function parseInlineStyle(styleAttr: string | null | undefined): Record<string, string> {
  const result: Record<string, string> = {};

  if (!styleAttr?.trim()) {
    return result;
  }

  for (const declaration of styleAttr.split(';')) {
    const colonIndex = declaration.indexOf(':');

    if (colonIndex === -1) {
      continue;
    }

    const property = declaration.slice(0, colonIndex).trim();
    const value = declaration.slice(colonIndex + 1).trim();

    if (property && value) {
      result[property] = value;
    }
  }

  return result;
}

export function serializeInlineStyle(styles: Record<string, string>): string {
  return Object.entries(styles)
    .filter(([, value]) => !!value)
    .map(([property, value]) => `${property}: ${value}`)
    .join('; ');
}

function applyLinkUnderlineToStyle(
  styles: Record<string, string>,
  underline: LinkUnderlineValue | null | undefined,
  prefix: '--link' | '--active-link',
): void {
  const textDecorationKey = `${prefix}-text-decoration`;
  const textDecorationStyleKey = `${prefix}-text-decoration-style`;

  if (!underline || underline === LINK_STYLE_FROM_GLOBAL) {
    delete styles[textDecorationKey];
    delete styles[textDecorationStyleKey];
    return;
  }

  if (underline === UnderlineStyleType.none) {
    styles[textDecorationKey] = 'none';
    delete styles[textDecorationStyleKey];
    return;
  }

  styles[textDecorationKey] = 'underline';
  styles[textDecorationStyleKey] = underline;
}

function parseLinkUnderlineFromStyle(
  styles: Record<string, string>,
  prefix: '--link' | '--active-link',
): LinkUnderlineValue {
  const textDecorationKey = `${prefix}-text-decoration`;
  const textDecorationStyleKey = `${prefix}-text-decoration-style`;
  const textDecoration = styles[textDecorationKey];

  if (!textDecoration) {
    return LINK_STYLE_FROM_GLOBAL;
  }

  if (textDecoration === 'none') {
    return UnderlineStyleType.none;
  }

  const underlineStyle = styles[textDecorationStyleKey];

  if (
    underlineStyle &&
    Object.values(UnderlineStyleType).includes(underlineStyle as UnderlineStyleType)
  ) {
    return underlineStyle as UnderlineStyleType;
  }

  return LINK_STYLE_FROM_GLOBAL;
}

export function buildLinkStyleAttribute(values: LinkStylingFormValues): string | null {
  const styles: Record<string, string> = {};

  if (values.linkColor && values.linkColor !== LINK_STYLE_FROM_GLOBAL) {
    styles[HTML_EDITOR_LINK_STYLE_CSS_VARS.linkColor] = values.linkColor;
  }

  applyLinkUnderlineToStyle(styles, values.linkUnderline, '--link');

  if (values.mouseoverColor && values.mouseoverColor !== LINK_STYLE_FROM_GLOBAL) {
    styles[HTML_EDITOR_LINK_STYLE_CSS_VARS.activeLinkColor] = values.mouseoverColor;
  }

  applyLinkUnderlineToStyle(styles, values.mouseoverUnderline, '--active-link');

  const serialized = serializeInlineStyle(styles);

  return serialized || null;
}

export function applyLinkStylingToElement(element: Element, values: LinkStylingFormValues): void {
  const existingStyles = parseInlineStyle(element.getAttribute('style'));
  const nextStyles = Object.fromEntries(
    Object.entries(existingStyles).filter(([property]) => !LINK_STYLE_CSS_VAR_VALUES.has(property)),
  );
  const linkStyles = parseInlineStyle(buildLinkStyleAttribute(values));

  Object.assign(nextStyles, linkStyles);

  const serialized = serializeInlineStyle(nextStyles);

  if (!serialized) {
    element.removeAttribute('style');
    return;
  }

  element.setAttribute('style', serialized);
}

export function parseLinkStylingFromElement(element: Element): Partial<LinkStylingFormValues> {
  const styles = parseInlineStyle(element.getAttribute('style'));

  return {
    linkColor: styles[HTML_EDITOR_LINK_STYLE_CSS_VARS.linkColor] ?? LINK_STYLE_FROM_GLOBAL,
    linkUnderline: parseLinkUnderlineFromStyle(styles, '--link'),
    mouseoverColor: styles[HTML_EDITOR_LINK_STYLE_CSS_VARS.activeLinkColor] ?? LINK_STYLE_FROM_GLOBAL,
    mouseoverUnderline: parseLinkUnderlineFromStyle(styles, '--active-link'),
  };
}
